import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "../lib/map.css";
import { makeRepertoPin } from "./SwissTopoMap";

// Default Leaflet icon URLs (fix for webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Worldwide tile providers — free, no API key required.
const BASE_LAYERS = {
  osm: {
    label: "Mappa (OSM)",
    url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },
  satellite: {
    label: "Satellite",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
    maxZoom: 19,
  },
  topo: {
    label: "Topografica",
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution:
      'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>, &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
    maxZoom: 17,
  },
};

const ClickHandler = ({ onPick }) => {
  useMapEvents({
    click: (e) => onPick && onPick(e.latlng),
  });
  return null;
};

const FlyToHandler = ({ flyTo }) => {
  const map = useMap();
  useEffect(() => {
    if (flyTo && Array.isArray(flyTo) && flyTo[0] != null && flyTo[1] != null) {
      map.flyTo(flyTo, Math.max(map.getZoom(), 14), { duration: 0.8 });
    }
  }, [flyTo, map]);
  return null;
};

const BaseSwitcher = ({ baseId, setBaseId }) => {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className={`layer-switcher ${collapsed ? "collapsed" : ""}`}>
      <button
        type="button"
        className="switcher-toggle"
        onClick={() => setCollapsed((v) => !v)}
        data-testid="world-layer-toggle"
      >
        <span>Sfondo</span>
        <span className="toggle-icon">▾</span>
      </button>
      <div className="switcher-body">
        {Object.entries(BASE_LAYERS).map(([id, lyr]) => (
          <label className="layer-row" key={id}>
            <input
              type="radio"
              name="worldbase"
              value={id}
              checked={baseId === id}
              onChange={() => setBaseId(id)}
            />
            <span>{lyr.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

/**
 * Worldwide map for picking / displaying an artifact's location.
 * Uses OpenStreetMap (default), with optional Satellite and Topographic layers.
 * No API key required.
 *
 * Props mirror SwissTopoMap so it's a drop-in replacement for LocationPicker and
 * artifact-detail location display.
 */
export default function WorldMap({
  markers = [],
  center,
  zoom,
  pickable = false,
  onPick,
  pickedPosition = null,
  flyTo = null,
  height = "min(calc(100dvh - 280px), 520px)",
}) {
  const [baseId, setBaseId] = useState("satellite");
  const initialCenter = center || (pickedPosition && pickedPosition[0] != null ? pickedPosition : [45.5, 9.2]);
  const initialZoom = zoom ?? (pickedPosition ? 14 : 5);
  const mapRef = useRef(null);
  const base = BASE_LAYERS[baseId];

  return (
    <div className="relative" style={{ height }}>
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        style={{ height: "100%", width: "100%" }}
        ref={mapRef}
        scrollWheelZoom={true}
        worldCopyJump={true}
      >
        <TileLayer
          url={base.url}
          attribution={base.attribution}
          maxZoom={base.maxZoom}
          key={baseId}
        />
        {markers.map((m, i) => (
          <Marker
            key={`${m.code || "m"}-${m.position[0]}-${m.position[1]}-${i}`}
            position={m.position}
            icon={makeRepertoPin(m.code)}
          />
        ))}
        {pickedPosition && pickedPosition[0] != null && (
          <Marker position={pickedPosition} />
        )}
        {pickable && <ClickHandler onPick={onPick} />}
        <FlyToHandler flyTo={flyTo} />
      </MapContainer>
      <BaseSwitcher baseId={baseId} setBaseId={setBaseId} />
      {pickable && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[500] bg-[#1E2520]/90 text-white text-xs px-3 py-1.5 rounded-sm pointer-events-none">
          Tocca un punto sulla mappa per posizionare il reperto
        </div>
      )}
    </div>
  );
}
